import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Wrench, Clock } from 'lucide-react';

/**
 * Modal de manutenção mostrado por cima da tela de login.
 * - Ao clicar "Entendido", o modal fecha e libera o formulário de login.
 * - O parent (LoginPage) reabre o modal quando o backend devolve 503
 *   numa tentativa de login feita por usuário NÃO Super Admin.
 */
export default function MaintenanceModal({ open, status, onClose }) {
  const [countdown, setCountdown] = useState('');

  useEffect(() => {
    if (!open || !status?.estimated_end_at) {
      setCountdown('');
      return;
    }
    const tick = () => {
      const end = new Date(status.estimated_end_at).getTime();
      const now = Date.now();
      const diff = end - now;
      if (diff <= 0) {
        setCountdown('A manutenção está demorando mais que o previsto, aguarde.');
        return;
      }
      const totalMin = Math.floor(diff / 60000);
      const h = Math.floor(totalMin / 60);
      const m = totalMin % 60;
      if (h > 0) {
        setCountdown(`em ${h}h ${m.toString().padStart(2, '0')}min`);
      } else if (m > 0) {
        setCountdown(`em ${m} minuto${m === 1 ? '' : 's'}`);
      } else {
        const secs = Math.max(0, Math.floor(diff / 1000));
        setCountdown(`em menos de 1 minuto (${secs}s)`);
      }
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [open, status?.estimated_end_at]);

  const fmtDate = (iso) => {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleString('pt-BR', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
      });
    } catch {
      return iso;
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="maintenance-modal">
        <DialogHeader>
          <div className="flex justify-center mb-3">
            <div className="w-16 h-16 rounded-full bg-teal-100 flex items-center justify-center">
              <Wrench className="w-8 h-8 text-teal-700" />
            </div>
          </div>
          <DialogTitle className="text-center text-xl">
            Sistema em Manutenção
          </DialogTitle>
          <DialogDescription className="text-center pt-2 leading-relaxed">
            {status?.message || 'Estamos realizando melhorias no Watizap para você. Voltaremos em breve.'}
          </DialogDescription>
        </DialogHeader>

        {status?.estimated_end_at && (
          <div className="bg-secondary/50 rounded-lg p-4 space-y-1 my-2">
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <Clock className="w-4 h-4" />
              <span>Previsão de retorno</span>
            </div>
            <p className="text-base font-semibold text-center" data-testid="maintenance-eta-date">
              {fmtDate(status.estimated_end_at)}
            </p>
            {countdown && (
              <p className="text-xs text-muted-foreground text-center" data-testid="maintenance-eta-countdown">
                {countdown}
              </p>
            )}
          </div>
        )}

        <DialogFooter className="sm:justify-center pt-2">
          <Button
            type="button"
            onClick={onClose}
            className="w-full sm:w-auto sm:min-w-[160px]"
            data-testid="maintenance-modal-ack-btn"
          >
            Entendido
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
